import React, { useState, useActionState, useEffect, useTransition } from 'react';
import { User, Mail, Phone, Building2, MapPin, Briefcase, Globe, ArrowLeft, Home, Lock, Shield } from 'lucide-react';
import { CustomDropdown, LanguageSwitcher, ImageUpload, FormInput, AddressAutocomplete, SubmitButton } from '../ui';
import logo from '../../assets/logo.png';
import { translations } from '../../i18n';
import { useOCR } from '../../hooks/useOCR';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { createPeople, updatePeople, getAllPeople } from '../../services/apiService';
import { addUserContact } from '../../services/userContactsService';
import { uploadDocument, DOCUMENT_TYPES } from '../../services/filesService';
import { encryptPassword, validatePassword } from '../../utils/encryption';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import AuthButton from '../auth/AuthButton';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

const defaultInitialData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    address: '',
    street: '',
    number: '',
    box: '',
    postalCode: '',
    city: '',
    country: '',
    countryCode: '',
    googlePlaceId: '',
    googleUrl: '',
    cityId: null,
    countryId: null,
    latitude: null,
    longitude: null,
    website: '',
    password: '',
    role: 'PRIVATE',
    homeNeeds: []
};

export default function PeopleForm({ onBack, isAdmin = false, initialData = null, onSubmit: externalSubmit = null, onLogin, isAuthenticated = false, onLogout, isEditMode = false, showHeader = true }) {
    const [language, setLanguage] = useState('fr');

    const lang = translations[language] || translations['fr'];
    // Convenience aliases matching previous usage
    const t = {
        ...lang.form,
        validation: lang.validation,
        messages: lang.form?.messages || {},
        homeNeedsOptions: lang.homeNeedsOptions || {}
    };

    // Convert any phone number to E.164 format using libphonenumber-js
    // This handles all international formats automatically
    const convertToE164 = (phone, defaultCountry = 'BE') => {
        if (!phone) return '';

        try {
            // Try to parse the phone number with default country
            const phoneNumber = parsePhoneNumber(phone, defaultCountry);

            if (phoneNumber && phoneNumber.isValid()) {
                // Return in E.164 format (e.g., +32429896067)
                return phoneNumber.format('E.164');
            }
        } catch (error) {
            // If parsing fails, try without default country (for numbers with country code)
            try {
                const phoneNumber = parsePhoneNumber(phone);
                if (phoneNumber && phoneNumber.isValid()) {
                    return phoneNumber.format('E.164');
                }
            } catch (e) {
                console.warn('Could not parse phone number:', phone);
            }
        }

        // Return original if parsing fails
        return phone;
    };

    // Load Google Maps API
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const isGoogleMapsLoaded = useGoogleMaps(googleMapsApiKey);

    const {
        formData,
        errors,
        handleChange,
        validate,
        resetForm,
        setFormData
    } = useFormValidation(initialData || defaultInitialData);

    // Update form data when initialData changes (e.g. async load in admin)
    useEffect(() => {
        if (initialData) {
            // Convert phone number to E.164 format if needed
            const convertedData = {
                ...initialData,
                phone: initialData.phone ? convertToE164(initialData.phone) : ''
            };
            setFormData(convertedData);
        }
    }, [initialData, setFormData]);

    const {
        isProcessing,
        imagePreview,
        message: ocrMessage,
        processImage,
        clearImage,
        setMessage: setOcrMessage
    } = useOCR(language);

    // Store the business card image file for later upload
    const [businessCardFile, setBusinessCardFile] = useState(null);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];

        // Store the file for later upload as business card
        if (file) {
            setBusinessCardFile(file);
        }

        const extractedData = await processImage(file);

        if (extractedData) {
            // Convert phone number to E.164 format if extracted
            const convertedData = {
                ...extractedData,
                phone: extractedData.phone ? convertToE164(extractedData.phone) : extractedData.phone
            };
            setFormData(prev => ({ ...prev, ...convertedData }));
        }
    };

    // Upload business card after person is created/updated
    const uploadBusinessCard = async (peopleUuid, peopleId, uploadedById) => {
        if (!businessCardFile) return;

        try {
            await uploadDocument(
                businessCardFile,
                peopleUuid,
                peopleId,
                DOCUMENT_TYPES.BUSINESS_CARD,
                uploadedById,
                'Carte de visite scannée'
            );
        } catch (error) {
            console.error('Error uploading business card:', error);
            // Don't throw - we don't want to fail the whole operation if just the card upload fails
        }
    };

    // Form Action Logic
    const handleFormSubmit = async (prevState, formDataObj) => {
        // Validation
        if (!validate()) {
            return {
                success: false,
                type: 'error',
                message: t.validation.required
            };
        }

        // Validate password - required in admin mode for new members only
        if (isAdmin && !isEditMode && !formData.password) {
            return {
                success: false,
                type: 'error',
                message: t.validation.accessCodeRequired || 'Access code required'
            };
        }

        // Validate password format if provided
        if (formData.password && !validatePassword(formData.password)) {
            return {
                success: false,
                type: 'error',
                message: t.validation.invalidPassword || 'Invalid password'
            };
        }

        try {
            // Encrypt password before sending to API
            const dataToSend = { ...formData };
            if (formData.password && formData.password.trim()) {
                dataToSend.encryptedPassword = encryptPassword(formData.password);
            }
            // Always remove plain password field (never send to API)
            delete dataToSend.password;


            // If external submit handler is provided (Admin mode), use it
            if (externalSubmit) {
                const result = await externalSubmit(dataToSend);

                // Map admin result to form state format if needed
                if (result && (result.success || result.SUCCESS)) {
                    // Wait a bit before calling back to let user see success message
                    setTimeout(() => onBack?.(), 1500);
                    return {
                        success: true,
                        type: 'success',
                        message: t.messages.saved
                    };
                } else {
                    return {
                        success: false,
                        type: 'error',
                        message: result?.message || result?.MESSAGE || ('Error saving')
                    };
                }
            }

            // Default Logic (Public Mode)
            // Check if person already exists in database (search by email ONLY - email must be unique)

            const allPeople = await getAllPeople({ limit: 1000, search: dataToSend.email });

            // Match on email ONLY (email is unique constraint)
            const existingPerson = allPeople.find(person =>
                person.email.toLowerCase() === dataToSend.email.toLowerCase()
            );

            if (existingPerson) {

                // Build update object with only NEW fields (non-empty in form, empty in DB)
                const updateData = {};
                let hasNewFields = false;

                for (const [key, value] of Object.entries(dataToSend)) {
                    // Skip email (unique identifier, cannot be changed)
                    if (key === 'email') continue;

                    // If form has a value and DB field is empty, add to update
                    const formValue = value?.trim() || '';
                    const dbValue = existingPerson[key]?.trim() || '';

                    if (formValue && !dbValue) {
                        updateData[key] = formValue;
                        hasNewFields = true;
                    }
                }

                if (hasNewFields) {
                    const existingUuid = existingPerson.peopleUuid || existingPerson.peopleuuid || existingPerson.PEOPLEUUID;
                    const existingId = existingPerson.id || existingPerson.ID;
                    const result = await updatePeople(existingUuid, updateData);

                    if (result.success) {
                        // Upload business card if available
                        if (businessCardFile && existingUuid) {
                            const uploadedById = localStorage.getItem('userId') || existingId;
                            await uploadBusinessCard(existingUuid, existingId, uploadedById);
                        }

                        setTimeout(() => onBack?.(), 2000);
                        return {
                            success: true,
                            type: 'success',
                            message: `${t.messages.updatedSuccess} ${Object.keys(updateData).length} ${t.messages.updatedSuccessFields}`
                        };
                    }
                } else {
                    return {
                        success: false,
                        type: 'info',
                        message: t.messages.alreadyExists
                    };
                }
            } else {
                // Person doesn't exist, create new
                const result = await createPeople(dataToSend);

                if (result.success || result.SUCCESS) {
                    const newPersonUuid = result.data?.peopleUuid || result.DATA?.PEOPLEUUID;
                    const newPersonId = result.data?.id || result.DATA?.ID;

                    // Upload business card if available
                    if (businessCardFile && newPersonUuid) {
                        const uploadedById = localStorage.getItem('userId') || newPersonId;
                        await uploadBusinessCard(newPersonUuid, newPersonId, uploadedById);
                    }

                    // If user is authenticated, add the new contact to their list
                    if (isAuthenticated) {
                        const currentUserId = localStorage.getItem('userId');
                        const newContactPeopleId = newPersonId;
                        if (currentUserId && newContactPeopleId) {
                            await addUserContact(parseInt(currentUserId), { peopleId: parseInt(newContactPeopleId) });
                        }
                    }

                    setTimeout(() => onBack?.(), 2000);
                    return {
                        success: true,
                        type: 'success',
                        message: isAuthenticated ? t.messages.contactAdded : t.messages.personCreated
                    };
                }
            }
        } catch (error) {
            console.error('Error saving person:', error);

            let errorMessage = t.error;
            if (error.status === 401) {
                errorMessage = t.messages.authError;
            } else if (error.message) {
                errorMessage = `${t.error}: ${error.message}`;
            }

            return {
                success: false,
                type: 'error',
                message: errorMessage
            };
        }
    };

    // React 19: Form Action with useActionState
    const [actionState, formAction, isPending] = useActionState(
        handleFormSubmit,
        { success: null, type: '', message: '' }
    );

    // Use transition for handling form submission
    const [isTransitionPending, startTransition] = useTransition();

    const handleSubmit = (e) => {
        e.preventDefault();
        startTransition(() => {
            formAction();
        });
    };

    return (
        <div className={`bg-white ${isAdmin ? '' : showHeader ? 'py-4 px-0 sm:px-4' : 'py-12 px-0 sm:px-4'} relative`}>
            {/* Header with Back button and Language Switcher - Only in Public Mode with showHeader */}
            {!isAdmin && showHeader && (
                <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
                    {onBack ? (
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 text-cabaza-purple hover:text-cabaza-pink transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            {language === 'fr' ? 'Retour' : 'Back'}
                        </button>
                    ) : (
                        <div></div>
                    )}
                    <div className="flex gap-2 items-center">
                        <LanguageSwitcher
                            language={language}
                            onLanguageChange={setLanguage}
                        />
                        <AuthButton
                            language={language}
                            onLoginClick={onLogin}
                            isAuthenticated={isAuthenticated}
                            onLogout={onLogout}
                        />
                    </div>
                </div>
            )}

            <div className={`${isAdmin ? '' : 'sm:max-w-2xl mx-auto'}`}>
                <div className={`${isAdmin ? '' : 'bg-white rounded-lg p-0 sm:p-8'}`}>

                    {!isAdmin && showHeader && (
                        <>
                            <div className="flex flex-col items-center mb-2 sm:mb-6">
                                <img src={logo} alt="Cabaza Logo" className="h-20 sm:h-32 mb-1 sm:mb-4" />
                            </div>
                            <h1 className="text-xl sm:text-4xl font-bold text-cabaza-purple mb-0 sm:mb-2 text-center">
                                {t.title}
                            </h1>
                            <p className="text-gray-600 mb-2 sm:mb-8 text-center text-xs sm:text-base">{t.subtitle}</p>
                        </>
                    )}

                    <ImageUpload
                        onImageUpload={handleImageUpload}
                        isProcessing={isProcessing}
                        imagePreview={imagePreview}
                    />

                    {/* Display OCR messages */}
                    {ocrMessage.text && (
                        <div className={`mb-6 p-4 rounded-lg ${{ success: 'bg-green-50 text-green-800', info: 'bg-blue-50 text-blue-800', warning: 'bg-yellow-50 text-yellow-800' }[ocrMessage.type] ?? 'bg-red-50 text-red-800'}`}>
                            {ocrMessage.text}
                        </div>
                    )}

                    {/* React 19: Display form action state messages */}
                    {actionState.message && (
                        <div className={`mb-6 p-4 rounded-lg ${{ success: 'bg-green-50 text-green-800', info: 'bg-blue-50 text-blue-800', warning: 'bg-yellow-50 text-yellow-800' }[actionState.type] ?? 'bg-red-50 text-red-800'}`}>
                            {actionState.message}
                        </div>
                    )}

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput
                                icon={User}
                                label={t.firstName}
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                error={errors.firstName}
                                required
                            />
                            <FormInput
                                icon={User}
                                label={t.lastName}
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                error={errors.lastName}
                                required
                            />
                        </div>

                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <Mail className="w-4 h-4 mr-2 text-cabaza-purple" />
                                {t.email} *
                            </label>
                            <input
                                type="email"
                                name="email"
                                aria-label={t.email}
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-cabaza-purple ${errors.email ? 'border-cabaza-pink' : 'border-gray-300'}`}
                            />
                            {errors.email && <p className="mt-1 text-xs text-cabaza-pink">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                {t.phone} {t.optional}
                            </label>
                            <PhoneInput
                                placeholder={t.phone}
                                aria-label={t.phone}
                                value={formData.phone ? convertToE164(formData.phone) : undefined}
                                onChange={(value) => handleChange('phone', value)}
                                className={`w-full ${errors.phone ? 'phone-input-error' : ''}`}
                                defaultCountry="BE"
                                international
                                withCountryCallingCode
                            />
                            {errors.phone && (
                                <p className="mt-1 text-xs text-cabaza-pink">{errors.phone}</p>
                            )}
                        </div>

                        <FormInput
                            icon={Building2}
                            label={`${t.company} ${t.optional}`}
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                        />

                        <FormInput
                            icon={Briefcase}
                            label={`${t.position} ${t.optional}`}
                            name="position"
                            value={formData.position}
                            onChange={handleChange}
                        />

                        {isGoogleMapsLoaded ? (
                            <AddressAutocomplete
                                label={`${t.address} ${t.optional}`}
                                value={formData.address}
                                onChange={handleChange}
                                onCoordinatesChange={(lat, lng) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        latitude: lat,
                                        longitude: lng
                                    }));
                                }}
                                onAddressComponentsChange={(components) => {
;
                                    setFormData(prev => ({
                                        ...prev,
                                        street: components.street || '',
                                        number: components.number || '',
                                        box: components.box || '',
                                        postalCode: components.postalCode || components.zip || '',
                                        city: components.city || '',
                                        country: components.country || '',
                                        countryCode: components.countryCode || '',
                                        googlePlaceId: components.googlePlaceId || components.placeId || '',
                                        googleUrl: components.googleUrl || ''
                                        // TODO: cityId and countryId will be looked up from database
                                    }));
                                }}
                                error={errors.address}
                                apiKey={googleMapsApiKey}
                            />
                        ) : (
                            <FormInput
                                icon={MapPin}
                                label={`${t.address} ${t.optional}`}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                            />
                        )}

                        {/* Hidden fields for latitude and longitude */}
                        <input type="hidden" name="latitude" value={formData.latitude || ''} />
                        <input type="hidden" name="longitude" value={formData.longitude || ''} />

                        {/* Website with Protocol Selector */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <Globe className="w-4 h-4 mr-2 text-cabaza-purple" />
                                {t.website} {t.optional}
                            </label>
                            <div className="relative flex items-stretch">
                                {/* Custom Protocol Dropdown */}
                                <CustomDropdown
                                    value={formData.website?.startsWith('http://') ? 'http://' : 'https://'}
                                    onChange={(protocol) => {
                                        const currentUrl = formData.website || '';
                                        const currentDomain = currentUrl.replace(/^https?:\/\//, '');
                                        handleChange('website', currentDomain ? `${protocol}${currentDomain}` : '');
                                    }}
                                    options={[
                                        { value: 'https://', label: 'https://' },
                                        { value: 'http://', label: 'http://' }
                                    ]}
                                    className="pl-3 pr-2 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg focus:outline-none focus:border-cabaza-purple h-full"
                                />
                                <input
                                    type="text"
                                    name="website_domain"
                                    value={formData.website ? formData.website.replace(/^https?:\/\//, '') : ''}
                                    onChange={(e) => {
                                        const domain = e.target.value.trim();
                                        // If domain is empty, clear website entirely
                                        if (!domain) {
                                            handleChange('website', '');
                                            return;
                                        }
                                        const currentUrl = formData.website || '';
                                        const protocol = currentUrl.startsWith('http://') ? 'http://' : 'https://';
                                        handleChange('website', `${protocol}${domain}`);
                                    }}
                                    className={`flex-1 px-4 py-2 border rounded-r-lg focus:outline-none focus:ring-0 ${errors.website ? 'border-cabaza-pink focus:border-cabaza-purple' : 'border-gray-300 focus:border-cabaza-purple'
                                        } text-sm`}
                                    style={{ minWidth: 0 }}
                                    placeholder="www.example.com"
                                />
                            </div>
                            {errors.website && (
                                <p className="mt-1 text-xs text-cabaza-pink">{errors.website}</p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                <Lock className="w-4 h-4 mr-2 text-cabaza-purple" />
                                {t.password} {isAdmin && !isEditMode ? '*' : isEditMode ? '(Optionnel - laisser vide pour garder l\'existant)' : t.optional}
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={(e) => handleChange('password', e.target.value)}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-cabaza-purple ${
                                    errors.password ? 'border-cabaza-pink' : 'border-gray-300'
                                }`}
                                placeholder="••••••"
                                maxLength="6"
                                pattern="\d{6}"
                                inputMode="numeric"
                                required={isAdmin && !isEditMode}
                            />
                            {errors.password && (
                                <p className="mt-1 text-xs text-cabaza-pink">{errors.password}</p>
                            )}
                            <p className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                                {isAdmin && !isEditMode
                                    ? '⚠️ Obligatoire : Code à 6 chiffres uniquement'
                                    : isEditMode
                                    ? '💡 Entrez 6 chiffres pour changer le code, ou laissez vide pour conserver l\'actuel'
                                    : t.passwordHelp}
                            </p>
                        </div>

                        {/* Role Field - Only for Admin */}
                        {isAdmin && (
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                    <Shield className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Rôle *
                                </label>
                                <CustomDropdown
                                    value={formData.role || 'PRIVATE'}
                                    onChange={(value) => handleChange('role', value)}
                                    options={[
                                        { value: 'PRIVATE', label: 'PRIVATE - Utilisateur privé (par défaut)' },
                                        { value: 'PUBLIC', label: 'PUBLIC - Profil visible publiquement' },
                                        { value: 'ADMIN', label: 'ADMIN - Administrateur' }
                                    ]}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-cabaza-purple bg-white"
                                />
                                <p className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    💡 PRIVATE: Accès standard | PUBLIC: Profil visible | ADMIN: Accès administration
                                </p>
                            </div>
                        )}

                        {/* Home Needs Checkboxes */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                                <Home className="w-4 h-4 mr-2 text-cabaza-purple" />
                                {t.homeNeeds} {t.optional}
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.entries(t.homeNeedsOptions).map(([key, label]) => (
                                    <label key={key} className="flex items-center space-x-2 cursor-pointer group min-h-11 sm:min-h-0">
                                        <input
                                            type="checkbox"
                                            checked={formData.homeNeeds?.includes(key) || false}
                                            onChange={(e) => {
                                                const newHomeNeeds = e.target.checked
                                                    ? [...(formData.homeNeeds || []), key]
                                                    : (formData.homeNeeds || []).filter(need => need !== key);
                                                handleChange('homeNeeds', newHomeNeeds);
                                            }}
                                            className="w-5 h-5 sm:w-4 sm:h-4 text-cabaza-purple border-gray-300 rounded focus:ring-cabaza-purple focus:ring-2"
                                        />
                                        <span className="text-sm text-gray-700 group-hover:text-cabaza-purple transition-colors">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* React 19: Use formAction and isPending from useActionState */}
                        <SubmitButton
                            onClick={handleSubmit}
                            disabled={isPending || isProcessing || isTransitionPending}
                            loading={isPending || isTransitionPending}
                        >
                            {(isPending || isTransitionPending) ? t.submitting : (isAdmin ? 'Enregistrer' : t.submit)}
                        </SubmitButton>

                        {isAdmin && (
                            <div className="text-right">
                                <button
                                    type="button"
                                    onClick={onBack}
                                    className="text-gray-500 hover:text-gray-700 text-sm underline"
                                >
                                    Annuler
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
